import React, { useEffect, useRef, useCallback } from "react";
import { TextEditorModalProps } from "../../types/modal";
import { countLines, isValidURL } from "../../utils/common-utils";
import { PiKeyReturnLight } from "react-icons/pi";
import { MdOutlineKeyboardCapslock } from "react-icons/md";

export default function TextEditorModal(props: Readonly<TextEditorModalProps>) {
  const isWarning = props.text.length >= props.characterWarningLength;
  const isCharLengthError = props.text.length === props.characterBlockLength;
  const isInvalidUrlError = !isValidURL(props.hyperlink);

  const isError = isCharLengthError || isInvalidUrlError;

  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Handle backdrop click
  const handleBackdropClick = useCallback((e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      props.onClose();
    }
  }, [props]);

  /**
   * Handle Enter key press & Ctrl + Enter key press
   * Ctrl + Enter - Save the text & add a new line
   * Enter - Save the text
   * @param e - Keyboard event
   * @returns void
   */
  const handleTextareaKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter") {
      if (e.ctrlKey) {
        // Allow new line when Ctrl + Enter is pressed
        e.preventDefault(); // Prevent default behavior of Enter key
        props.handleSave(); // Call the save function
      } else {
        return; // Do nothing, let the default behavior occur
      }
    }
  };

  useEffect(() => {
    props.open ? document.body.style.overflow = 'hidden' : document.body.style.overflow = 'unset';
    if (props.open && textareaRef.current) {
      textareaRef.current.focus(); // Focus the textarea when the modal opens
    }
  }, [props.open]);

  if (!props.open) return null;

  return (
    <div className="modal-overlay" onClick={handleBackdropClick}>
      <div className="modal-content">
        <div className="modal-header">
          <h2 className="modal-title">
            Update Text
          </h2>
        </div>
        <div className="modal-body">
          <div className="form-wrapper">
            <div className="input-wrapper">
              <label htmlFor="text-field" className="form-label text-start">
                Text Content
              </label>
              <textarea
                id="text-field"
                value={props.text}
                onChange={(e) => props.onStringChange(e.target.value)}
                className={`form-control ${isCharLengthError ? "error" : ""}`}
                rows={Math.max(countLines(props.text) + 2, 5)}
                maxLength={props.characterBlockLength}
                onKeyDown={handleTextareaKeyDown}
                ref={textareaRef}
                placeholder="Enter your text here..."
              />
              <div className="text-end mt-2">
                <span className={`character-count ${isWarning ? "warning" : isError ? "error" : ""}`}>
                  {props.text.length}/{props.characterBlockLength - 1}
                </span>
              </div>
            </div>
            
            <div className="input-wrapper">
              <label htmlFor="hyperlink-field" className="form-label text-start">
                Hyperlink
              </label>
              <input
                type="text"
                id="hyperlink-field"
                placeholder="Leave empty to remove"
                className={`form-control ${isInvalidUrlError ? "error" : ""}`}
                value={props.hyperlink}
                onChange={(e) => props.onHyperlinkChange(e.target.value)}
              />
            </div>
          </div>
          
          {!isCharLengthError && isWarning && (
            <div className="alert alert-warning mt-3">
              <span className="warning-text">
                ⚠️ Warning: This character count might break the design!
              </span>
            </div>
          )}
          {isCharLengthError && (
            <div className="alert alert-error mt-3">
              <span className="error-text">
                ❌ Error: Character limit exceeded!
              </span>
            </div>
          )}
          {isInvalidUrlError && (
            <div className="alert alert-error mt-3">
              <span className="error-text">
                ❌ Error: URL is invalid!
              </span>
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={props.onClose}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={isError}
            onClick={props.handleSave}
            title="Ctrl + Enter to save"
          >
            <span className="btn-content">
              <span>Save</span>
              <MdOutlineKeyboardCapslock />
              <PiKeyReturnLight />
            </span>
          </button>
        </div>
      </div>
    </div>
  );
}
